'use client'

import {Command as CommandPrimitive} from 'cmdk'
import * as React from 'react'
import {useState} from 'react'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'
import SearchIconInputSmallChangeColor from 'src/images/SearchIconInputSmall'

const Command = React.forwardRef<React.ElementRef<typeof CommandPrimitive>, React.ComponentPropsWithoutRef<typeof CommandPrimitive>>(
  ({className, ...props}, ref) => (
    <CommandPrimitive
      ref={ref}
      style={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...props.style,
      }}
      {...props}
    />
  ),
)
Command.displayName = CommandPrimitive.displayName

const CommandInput = React.forwardRef<React.ElementRef<typeof CommandPrimitive.Input>, React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>>(
  ({className, ...props}, ref) => (
    <div style={{position: 'relative', display: 'flex', alignItems: 'center'}} cmdk-input-wrapper=''>
      <span style={{position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', zIndex: 10}}>
        <SearchIconInputSmallChangeColor />
      </span>
      <CommandPrimitive.Input
        ref={ref}
        style={{
          boxSizing: 'border-box',
          display: 'flex',
          maxHeight: '2.5rem',
          width: '100%',
          borderRadius: '0.375rem',
          border: '1px solid var(--input-border)',
          backgroundColor: 'transparent',
          padding: '1rem',
          fontSize: '0.875rem',
          fontWeight: 'normal',
          color: 'var(--gray-900)',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          position: 'relative',
          height: '2.5rem',
          paddingLeft: '3rem',

          ...props.style,
        }}
        {...props}
      />
    </div>
  ),
)

CommandInput.displayName = CommandPrimitive.Input.displayName

const CommandLoading = React.forwardRef<React.ElementRef<typeof CommandPrimitive.Loading>, React.ComponentPropsWithoutRef<typeof CommandPrimitive.Loading>>(
  ({className, ...props}, ref) => (
    <CommandPrimitive.Loading
      ref={ref}
      style={{
        display: 'flex',
        width: '100%',
        zIndex: 50,
        backgroundColor: 'white',
        alignItems: 'center',
        justifyContent: 'center',
        ...props.style,
      }}
      {...props}
    />
  ),
)
CommandLoading.displayName = CommandPrimitive.Loading.displayName

const CommandList = React.forwardRef<React.ElementRef<typeof CommandPrimitive.List>, React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>>(
  ({className, ...props}, ref) => (
    <CommandPrimitive.List
      ref={ref}
      style={{
        maxHeight: '400px',
        overflowY: 'auto',
        height: '100px',
        ...props.style,
      }}
      {...props}
    />
  ),
)

CommandList.displayName = CommandPrimitive.List.displayName

const CommandEmpty = React.forwardRef<React.ElementRef<typeof CommandPrimitive.Empty>, React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>>(
  (props, ref) => (
    <CommandPrimitive.Empty
      ref={ref}
      style={{
        padding: '1.5rem',
        textAlign: 'center',
        fontSize: '0.875rem',
        color: 'var(--gray-900)',
        ...props.style,
      }}
      {...props}
    />
  ),
)

CommandEmpty.displayName = CommandPrimitive.Empty.displayName

const CommandGroup = React.forwardRef<React.ElementRef<typeof CommandPrimitive.Group>, React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>>(
  ({className, ...props}, ref) => (
    <CommandPrimitive.Group
      ref={ref}
      style={{
        overflow: 'hidden',
        padding: '0.25rem',
        color: 'var(--foreground)',
        ...props.style,
      }}
      {...props}
    />
  ),
)

CommandGroup.displayName = CommandPrimitive.Group.displayName

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({className, ...props}, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    style={{
      margin: '0 -0.25rem',
      height: '1px',
      backgroundColor: 'var(--border)',
      ...props.style,
    }}
    {...props}
  />
))
CommandSeparator.displayName = CommandPrimitive.Separator.displayName

const CommandItem = React.forwardRef<React.ElementRef<typeof CommandPrimitive.Item>, React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>>(
  ({className, ...props}, ref) => {
    const [hover, setHover] = useState(false)
    const {styledTheme} = usePersonalizedThemeByCompany()
    return (
      <CommandPrimitive.Item
        ref={ref}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          position: 'relative',
          display: 'flex',
          cursor: 'pointer',
          alignItems: 'center',
          gap: '0.2rem',
          fontSize: '12px',
          padding: '0.375rem 0.5rem',
          fontWeight: 'normal',
          borderRadius: '5px',
          background: hover ? styledTheme.hoverColors.secondary : 'white',
          ...props.style,
        }}
        {...props}></CommandPrimitive.Item>
    )
  },
)

CommandItem.displayName = CommandPrimitive.Item.displayName

const CommandShortcut = ({className, ...props}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      style={{
        marginLeft: 'auto',
        fontSize: '0.75rem',
        letterSpacing: '0.1em',
        color: 'black',
        ...props.style,
      }}
      {...props}
    />
  )
}
CommandShortcut.displayName = 'CommandShortcut'

export {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandLoading, CommandSeparator, CommandShortcut}
